import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';

import { Icono } from '#components/mobile/ui/Icono';
import { color } from '#components/mobile/ui/tokens';

import { CIFRAS_PIN } from './pin';

const FILAS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
];

/** Los cuatro puntos del PIN: llenos los que ya se han escrito. */
export function PuntosPin({
  escritas,
  error,
}: {
  escritas: number;
  error?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <View
      role="img"
      aria-label={t('{{escritas}} of {{total}} digits', {
        escritas,
        total: CIFRAS_PIN,
      })}
      style={{
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 18,
        animation: error ? 'rumbo-pin-sacude 0.4s' : undefined,
      }}
    >
      <style>{`@keyframes rumbo-pin-sacude {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-8px); }
        40%, 80% { transform: translateX(8px); }
      }`}</style>
      {Array.from({ length: CIFRAS_PIN }, (_, i) => (
        <View
          key={i}
          style={{
            width: 16,
            height: 16,
            borderRadius: 8,
            border: `2px solid ${error ? color.bad : color.accent}`,
            backgroundColor:
              i < escritas ? (error ? color.bad : color.accent) : 'transparent',
          }}
        />
      ))}
    </View>
  );
}

/**
 * Teclado numérico del PIN. Abajo a la izquierda va lo que se pase en
 * `izquierda` (la huella en la pantalla de bloqueo); a la derecha, borrar.
 */
export function TecladoPin({
  onCifra,
  onBorrar,
  izquierda,
  deshabilitado,
}: {
  onCifra: (cifra: string) => void;
  onBorrar: () => void;
  izquierda?: ReactNode;
  deshabilitado?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: 12, opacity: deshabilitado ? 0.4 : 1 }}>
      {FILAS.map(fila => (
        <View
          key={fila[0]}
          style={{ flexDirection: 'row', justifyContent: 'center', gap: 20 }}
        >
          {fila.map(c => (
            <Tecla
              key={c}
              onPress={() => onCifra(c)}
              isDisabled={deshabilitado}
            >
              {c}
            </Tecla>
          ))}
        </View>
      ))}
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 20 }}>
        <View style={{ width: 72, height: 72 }}>{izquierda}</View>
        <Tecla onPress={() => onCifra('0')} isDisabled={deshabilitado}>
          0
        </Tecla>
        <Tecla
          sinFondo
          onPress={onBorrar}
          isDisabled={deshabilitado}
          aria-label={t('Delete')}
        >
          <Icono nombre="bksp" size={26} />
        </Tecla>
      </View>
    </View>
  );
}

export function Tecla({
  children,
  onPress,
  isDisabled,
  sinFondo,
  'aria-label': ariaLabel,
}: {
  children: ReactNode;
  onPress: () => void;
  isDisabled?: boolean;
  sinFondo?: boolean;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={isDisabled}
      aria-label={ariaLabel}
      style={{
        width: 72,
        height: 72,
        borderRadius: 36,
        border: 0,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: sinFondo ? 'transparent' : color.surface,
        color: color.fg,
        fontSize: 28,
        fontWeight: 600,
        fontFamily: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        cursor: isDisabled ? 'default' : 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {children}
    </button>
  );
}
